import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Avatar, Badge, Button, Empty, Input, List, Popconfirm, Segmented, Space, Spin, Tag, Tooltip, Typography, message,
} from "antd";
import {
    BellOutlined, CheckOutlined, DeleteOutlined, DollarOutlined, FileTextOutlined, MailOutlined,
    MessageOutlined, ReloadOutlined, SearchOutlined, UndoOutlined, ArrowRightOutlined, SafetyOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/fr";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";

dayjs.extend(relativeTime);
dayjs.locale("fr");

const { Text, Title } = Typography;
const PRIMARY = "#242166";

const sansAccents = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Icône + couleur selon la nature (déduite du titre) — rend l'historique plus lisible.
const iconFor = (not) => {
    const t = sansAccents(not?.titre);
    if (t.includes("mention") || t.includes("discussion")) return { icon: <MessageOutlined />, color: "#764ba2" };
    if (t.includes("paiement") || t.includes("reglement") || t.includes("commission") || t.includes("decaiss"))
        return { icon: <DollarOutlined />, color: "#0e9f6e" };
    if (t.includes("sinistre") || t.includes("evaluation") || t.includes("expert"))
        return { icon: <FileTextOutlined />, color: "#c2410c" };
    if (t.includes("contrat") || t.includes("renouvel") || t.includes("agrement"))
        return { icon: <SafetyOutlined />, color: "#1890ff" };
    return { icon: <BellOutlined />, color: PRIMARY };
};

const emetteurNom = (not) => {
    const p = not?.emetteur?.personne;
    const n = `${p?.nom || ""} ${p?.prenom || ""}`.trim();
    return n || not?.emetteur?.username || null;
};

const NotificationsHistory = () => {
    const navigate = useNavigate();
    const username = LOGGED_USER?.username;

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtre, setFiltre] = useState("Toutes");
    const [search, setSearch] = useState("");
    const [busyId, setBusyId] = useState(null);

    const charger = () => {
        if (!username) return;
        setLoading(true);
        axiosInstance
            .get(`/api/notification/user/${username}`)
            .then((res) => setItems(Array.isArray(res.data) ? res.data : []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        charger();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const nbNonLues = useMemo(() => items.filter((n) => !n?.dateLecture).length, [items]);

    const filtres = useMemo(() => {
        const q = sansAccents(search.trim());
        return items.filter((n) => {
            if (filtre === "Non lues" && n?.dateLecture) return false;
            if (filtre === "Lues" && !n?.dateLecture) return false;
            if (!q) return true;
            return sansAccents(n?.titre).includes(q) || sansAccents(n?.contenu).includes(q);
        });
    }, [items, filtre, search]);

    const majLocal = (codeId, patch) =>
        setItems((prev) => prev.map((n) => (n.codeId === codeId ? { ...n, ...patch } : n)));

    const ouvrir = async (not) => {
        if (!not?.dateLecture) {
            axiosInstance.get(`/api/notification/read/${not.codeId}`).catch(() => { });
            majLocal(not.codeId, { dateLecture: new Date().toISOString() });
        }
        if (not?.cheminAcces) navigate(not.cheminAcces);
    };

    const basculerLecture = async (not) => {
        setBusyId(not.codeId);
        try {
            if (not?.dateLecture) {
                await axiosInstance.get(`/api/notification/unread-mark/${not.codeId}`);
                majLocal(not.codeId, { dateLecture: null });
            } else {
                await axiosInstance.get(`/api/notification/read/${not.codeId}`);
                majLocal(not.codeId, { dateLecture: new Date().toISOString() });
            }
        } catch {
            message.error("Action impossible");
        } finally {
            setBusyId(null);
        }
    };

    const supprimer = async (not) => {
        setBusyId(not.codeId);
        try {
            await axiosInstance.delete(`/api/notification/${not.codeId}`);
            setItems((prev) => prev.filter((n) => n.codeId !== not.codeId));
            message.success("Notification supprimée");
        } catch {
            message.error("Suppression impossible");
        } finally {
            setBusyId(null);
        }
    };

    const toutMarquerLu = async () => {
        try {
            await axiosInstance.put(`/api/notification/read-all/${username}`);
            const now = new Date().toISOString();
            setItems((prev) => prev.map((n) => (n.dateLecture ? n : { ...n, dateLecture: now })));
            message.success("Toutes les notifications sont marquées comme lues");
        } catch {
            message.error("Action impossible");
        }
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"NOTIFICATIONS"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #667eea 100%)`, borderRadius: 14, padding: "18px 22px", marginBottom: 18, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: "#fff" }}>
                                        <BellOutlined style={{ marginRight: 8 }} />Historique des notifications
                                    </Title>
                                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                        {items.length} notification(s) · {nbNonLues} non lue(s)
                                    </Text>
                                </div>
                                <Space wrap>
                                    <Button icon={<ReloadOutlined />} onClick={charger} loading={loading}
                                        style={{ borderColor: "rgba(255,255,255,0.4)", color: "#fff", background: "rgba(255,255,255,0.12)" }}>
                                        Rafraîchir
                                    </Button>
                                    <Button icon={<CheckOutlined />} onClick={toutMarquerLu} disabled={nbNonLues === 0}
                                        style={{ background: "#fff", color: PRIMARY, fontWeight: 600 }}>
                                        Tout marquer comme lu
                                    </Button>
                                </Space>
                            </div>

                            {/* Filtres */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
                                <Segmented
                                    value={filtre}
                                    onChange={setFiltre}
                                    options={[
                                        { label: `Toutes (${items.length})`, value: "Toutes" },
                                        { label: `Non lues (${nbNonLues})`, value: "Non lues" },
                                        { label: `Lues (${items.length - nbNonLues})`, value: "Lues" },
                                    ]}
                                />
                                <Input
                                    allowClear
                                    prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                                    placeholder="Rechercher…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    style={{ maxWidth: 300 }}
                                />
                            </div>

                            {/* Liste */}
                            <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", padding: 8 }}>
                                {loading ? (
                                    <div style={{ textAlign: "center", padding: 40 }}><Spin size="large" /></div>
                                ) : filtres.length === 0 ? (
                                    <Empty description="Aucune notification" style={{ padding: 40 }} />
                                ) : (
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={filtres}
                                        pagination={{ pageSize: 12, showSizeChanger: true, pageSizeOptions: ["12", "25", "50"], showTotal: (t) => `${t} notification(s)` }}
                                        renderItem={(not) => {
                                            const lu = !!not?.dateLecture;
                                            const { icon, color } = iconFor(not);
                                            const emetteur = emetteurNom(not);
                                            return (
                                                <List.Item
                                                    style={{ padding: "12px 14px", borderRadius: 10, background: lu ? "transparent" : "#f5f6fe", cursor: not?.cheminAcces ? "pointer" : "default", transition: "background .15s" }}
                                                    onClick={() => ouvrir(not)}
                                                    actions={[
                                                        <Tooltip key="rw" title={lu ? "Marquer non lue" : "Marquer lue"}>
                                                            <Button size="small" type="text" loading={busyId === not.codeId}
                                                                icon={lu ? <UndoOutlined /> : <CheckOutlined />}
                                                                onClick={(e) => { e.stopPropagation(); basculerLecture(not); }} />
                                                        </Tooltip>,
                                                        not?.cheminAcces ? (
                                                            <Tooltip key="go" title="Ouvrir">
                                                                <Button size="small" type="text" icon={<ArrowRightOutlined />}
                                                                    onClick={(e) => { e.stopPropagation(); ouvrir(not); }} />
                                                            </Tooltip>
                                                        ) : null,
                                                        <Popconfirm key="del" title="Supprimer cette notification ?" okText="Supprimer" cancelText="Annuler"
                                                            okButtonProps={{ danger: true }}
                                                            onConfirm={() => supprimer(not)} onCancel={(e) => e?.stopPropagation?.()}>
                                                            <Button size="small" type="text" danger icon={<DeleteOutlined />}
                                                                onClick={(e) => e.stopPropagation()} />
                                                        </Popconfirm>,
                                                    ].filter(Boolean)}
                                                >
                                                    <List.Item.Meta
                                                        avatar={
                                                            <Badge dot={!lu} offset={[-4, 4]}>
                                                                <Avatar shape="square" style={{ background: `${color}18`, color, borderRadius: 10 }} icon={icon} />
                                                            </Badge>
                                                        }
                                                        title={
                                                            <Space size={6} wrap>
                                                                <span style={{ fontWeight: lu ? 500 : 700, color: "#1f2937" }}>{not?.titre}</span>
                                                                {!lu && <Tag color="blue" style={{ margin: 0 }}>Nouveau</Tag>}
                                                                {not?.cheminAcces && <Tag style={{ margin: 0 }}>Cliquable</Tag>}
                                                            </Space>
                                                        }
                                                        description={
                                                            <div>
                                                                <div style={{ color: "#4b5563", fontSize: 13, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                                                    {not?.contenu}
                                                                </div>
                                                                <div style={{ color: "#9ca3af", fontSize: 12, marginTop: 3 }}>
                                                                    {emetteur ? <><MailOutlined /> {emetteur} · </> : null}
                                                                    {not?.createdAt ? dayjs(not.createdAt).fromNow() : ""}
                                                                    {lu && not?.dateLecture ? ` · lue ${dayjs(not.dateLecture).fromNow()}` : ""}
                                                                </div>
                                                            </div>
                                                        }
                                                    />
                                                </List.Item>
                                            );
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default NotificationsHistory;
